/** 元素根（壳）上的文字样式设置。 */
import {
  cn,
  ListBox,
  NumberField,
  Select,
  Spinner,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
} from '@heroui/react';
import { ColorPickerField } from '@/components/custom';
import {
  IconAlignCenter,
  IconAlignJustified,
  IconAlignLeft2,
  IconAlignRight2,
  IconBold,
} from '@tabler/icons-react';
import { useEffect, useState, type MouseEvent } from 'react';
import type { Selection } from 'react-aria-components';
import type { ParagraphStyle } from '@/components/canvas/document/publicTypes';
import {
  DESIGNER_FONT_OPTIONS,
  DESIGNER_FONT_WEIGHT_LABELS,
  DEFAULT_DESIGNER_FONT,
  fontLoadSpec,
  getDesignerFontOption,
  normalizeFontWeightNumber,
  resolveBoldFontWeight,
  resolveRegularFontWeight,
  resolveSupportedFontWeight,
} from '@/components/canvas/shared/constants/fonts';

/** value → 是否已可用（可显示该字体预览）；system-ui 恒为 true */
function useCanvasFontPreviewReady(): Record<string, boolean> {
  const [ready, setReady] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const { value: v } of DESIGNER_FONT_OPTIONS) {
      if (v === 'system-ui') {
        initial[v] = true;
        continue;
      }
      if (typeof document === 'undefined' || !document.fonts) {
        initial[v] = false;
        continue;
      }
      const spec = fontLoadSpec(v);
      try {
        initial[v] = document.fonts.check(spec);
      } catch {
        initial[v] = false;
      }
    }
    return initial;
  });

  useEffect(() => {
    if (typeof document === 'undefined' || !document.fonts) return;

    const markReady = (v: string) => {
      setReady((prev) => (prev[v] ? prev : { ...prev, [v]: true }));
    };

    for (const { value: v } of DESIGNER_FONT_OPTIONS) {
      if (v === 'system-ui') continue;
      const spec = fontLoadSpec(v);
      if (document.fonts.check(spec)) {
        markReady(v);
        continue;
      }
      void document.fonts.load(spec).then(
        () => markReady(v),
        () => markReady(v),
      );
    }

    const syncFromFaceSet = () => {
      for (const { value: v } of DESIGNER_FONT_OPTIONS) {
        if (v === 'system-ui') continue;
        const spec = fontLoadSpec(v);
        try {
          if (document.fonts.check(spec)) markReady(v);
        } catch {
          /* ignore */
        }
      }
    };

    document.fonts.addEventListener('loadingdone', syncFromFaceSet);
    return () => document.fonts.removeEventListener('loadingdone', syncFromFaceSet);
  }, []);

  return ready;
}

const DEFAULT_FONT_SIZE = 14;
const DEFAULT_TEXT_COLOR = '#000000';

/** 文字样式 patch：直接对应元素根 typography / paragraph 字段。 */
export type TextSettingsPatch = Partial<{
  fontFamily: string;
  fontWeight: number | string;
  fontSize: number;
  color: string;
  lineHeight: number;
  letterSpacing: number;
  textAlign: ParagraphStyle['textAlign'];
  verticalAlign: NonNullable<ParagraphStyle['verticalAlign']>;
  writingMode: 'horizontal-tb' | 'vertical-rl';
}>;

export type RichTextTextSettingsPatch = TextSettingsPatch;

export interface RichTextEditingOptions {
  isActive: boolean;
  selectionBold: boolean;
  onBoldPress: (pressed: boolean) => void;
}

function selectionToSingleKey(keys: Selection): string | undefined {
  if (keys === 'all') return undefined;
  const [first] = keys;
  return first != null ? String(first) : undefined;
}

export interface TextSettingsProps {
  className?: string;
  /** 区块标题，默认「文字」；传空字符串时不渲染标题（便于外层用 CardTitle） */
  title?: string;
  textColorDefaultValue?: string;
  value: {
    fontFamily: string;
    fontWeight: number | string | undefined;
    fontSize: number;
    textColor: string;
    textAlign?: ParagraphStyle['textAlign'];
    verticalAlign?: NonNullable<ParagraphStyle['verticalAlign']>;
    writingMode?: 'horizontal-tb' | 'vertical-rl';
    lineHeight?: number | string;
    letterSpacing?: number;
  };
  onChange: (patch: TextSettingsPatch) => void;
  /** 富文本编辑态：加粗优先作用选区 */
  richTextEditing?: RichTextEditingOptions;
}

export function TextSettings({
  className,
  title = '文字',
  textColorDefaultValue = DEFAULT_TEXT_COLOR,
  value,
  onChange,
  richTextEditing,
}: TextSettingsProps) {
  const labelClass = 'text-xs text-muted font-normal shrink-0 w-14';
  const fontFamily = value.fontFamily || DEFAULT_DESIGNER_FONT;
  const fontOption = getDesignerFontOption(fontFamily);
  const supportedWeights = fontOption.weights;
  const fontPreviewReady = useCanvasFontPreviewReady();
  const fontSize = value.fontSize ?? DEFAULT_FONT_SIZE;
  const textColor = value.textColor || textColorDefaultValue;
  const textAlign = value.textAlign ?? 'left';
  const verticalAlign = value.verticalAlign ?? 'middle';
  const writingMode = value.writingMode ?? 'horizontal-tb';
  const isVerticalWriting = writingMode === 'vertical-rl';
  const lineHeight = Math.max(0.1, Number(value.lineHeight ?? 1.2));
  const letterSpacingEm = Number.isFinite(Number(value.letterSpacing))
    ? Number(value.letterSpacing)
    : 0;

  const rawFontWeightNum = normalizeFontWeightNumber(value.fontWeight);
  const fontWeightNum = resolveSupportedFontWeight(fontFamily, value.fontWeight);

  const boldPressed =
    richTextEditing?.isActive
      ? richTextEditing.selectionBold
      : rawFontWeightNum >= 700;

  const setFontFamily = (v: string) => {
    const nextWeight = resolveSupportedFontWeight(v, value.fontWeight);
    onChange({ fontFamily: v, fontWeight: nextWeight });
  };

  const setFontWeight = (n: number) => {
    onChange({ fontWeight: resolveSupportedFontWeight(fontFamily, n) });
  };

  const setFontSize = (n: number) => {
    onChange({ fontSize: n });
  };
  const setLineHeight = (n: number) => {
    const v = Math.round(Math.max(0.1, n) * 10) / 10;
    onChange({ lineHeight: v });
  };
  const setLetterSpacingEm = (n: number) => {
    const v = Math.round(n * 10) / 10;
    onChange({ letterSpacing: v });
  };

  const setBold = (pressed: boolean) => {
    if (richTextEditing?.isActive) {
      richTextEditing.onBoldPress(pressed);
      return;
    }
    const w = pressed ? resolveBoldFontWeight(fontFamily) : resolveRegularFontWeight(fontFamily);
    onChange({ fontWeight: w });
  };

  const setFillColor = (next: string) => {
    if (next.startsWith('linear-gradient') || next.startsWith('radial-gradient')) return;
    onChange({ color: next });
  };

  const blockMouseDown = (e: MouseEvent) => {
    if (richTextEditing?.isActive) e.preventDefault();
  };

  useEffect(() => {
    if (rawFontWeightNum !== fontWeightNum) {
      onChange({ fontWeight: fontWeightNum });
    }
  }, [fontWeightNum, onChange, rawFontWeightNum]);

  return (
    <div className={cn('space-y-2 w-full', className)}>
      {title ? <p className="text-sm font-medium text-foreground">{title}</p> : null}
      {/* 字体 */}
      <div className="flex items-center gap-2">
        <label className={labelClass}>字体</label>
        <Select
          aria-label="字体"
          value={fontFamily}
          onChange={(key) => {
            if (key != null) setFontFamily(String(key));
          }}
          className="min-w-0 flex-1"
        >
          <Select.Trigger className="h-8 min-w-0 flex-1">
            <Select.Value />
            <Select.Indicator />
          </Select.Trigger>
          <Select.Popover>
            <ListBox>
              {DESIGNER_FONT_OPTIONS.map(({ value: v, label }) => {
                const showPreview = Boolean(fontPreviewReady[v]);
                return (
                  <ListBox.Item key={v} id={v} textValue={label}>
                    <span
                      className="flex min-w-0 items-center gap-2"
                      title={showPreview ? undefined : '字体加载中，可先选择'}
                    >
                      {!showPreview ? <Spinner size="sm" className="shrink-0" /> : null}
                      <span
                        className="min-w-0 truncate"
                        style={showPreview ? { fontFamily: v } : undefined}
                      >
                        {label}
                      </span>
                    </span>
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                );
              })}
            </ListBox>
          </Select.Popover>
        </Select>
      </div>
      {/* 字重 */}
      <div className="flex items-center gap-2">
        <label className={labelClass}>字重</label>
        <Select
          aria-label="字重"
          value={String(fontWeightNum)}
          onChange={(key) => {
            if (key != null) setFontWeight(Number(key));
          }}
          className="min-w-0 flex-1"
        >
          <Select.Trigger className="h-8 min-w-0 flex-1">
            <Select.Value />
            <Select.Indicator />
          </Select.Trigger>
          <Select.Popover>
            <ListBox>
              {supportedWeights.map((v) => (
                <ListBox.Item key={v} id={String(v)} textValue={DESIGNER_FONT_WEIGHT_LABELS[v]}>
                  <span style={{ fontFamily, fontWeight: v }}>
                    {DESIGNER_FONT_WEIGHT_LABELS[v]}
                  </span>
                  <ListBox.ItemIndicator />
                </ListBox.Item>
              ))}
            </ListBox>
          </Select.Popover>
        </Select>
      </div>
      {/* 字号 + 加粗 */}
      <div className="flex items-center gap-2">
        <label className={labelClass}>字号</label>
        <NumberField
          aria-label="字号"
          value={fontSize}
          onChange={(v) =>
            setFontSize(Number.isFinite(v) ? Math.max(1, Math.round(v)) : DEFAULT_FONT_SIZE)
          }
          minValue={1}
          maxValue={500}
          className="min-w-0 flex-1"
        >
          <NumberField.Group className="min-w-0 flex-1">
            <NumberField.DecrementButton />
            <NumberField.Input />
            <NumberField.IncrementButton />
          </NumberField.Group>
        </NumberField>
        <ToggleButton
          size="sm"
          variant="default"
          isIconOnly
          isSelected={boldPressed}
          onMouseDown={blockMouseDown}
          onPress={() => setBold(!boldPressed)}
          aria-label="加粗"
        >
          <IconBold className="size-4" stroke={1.5} />
        </ToggleButton>
      </div>
      <div className="flex items-center gap-2">
        <label className={labelClass}>行间距</label>
        <NumberField
          aria-label="行间距"
          value={lineHeight}
          onChange={(v) => setLineHeight(Number.isFinite(v) ? v : 1.2)}
          minValue={0.1}
          maxValue={10}
          step={0.1}
          className="min-w-0 flex-1"
        >
          <NumberField.Group className="min-w-0 flex-1">
            <NumberField.DecrementButton />
            <NumberField.Input />
            <NumberField.IncrementButton />
          </NumberField.Group>
        </NumberField>
      </div>
      <div className="flex items-center gap-2">
        <label className={labelClass}>字间距</label>
        <NumberField
          aria-label="字间距"
          value={letterSpacingEm}
          onChange={(v) => setLetterSpacingEm(Number.isFinite(v) ? v : 0)}
          minValue={-5}
          maxValue={100}
          step={0.1}
          className="min-w-0 flex-1"
        >
          <NumberField.Group className="min-w-0 flex-1">
            <NumberField.DecrementButton />
            <NumberField.Input />
            <NumberField.IncrementButton />
          </NumberField.Group>
        </NumberField>
        <span className="text-xs text-muted shrink-0">em</span>
      </div>
      <div className="flex items-center gap-2" onMouseDown={blockMouseDown}>
        <label className={labelClass}>颜色</label>
        <div className="min-w-0 flex-1">
          <ColorPickerField
            aria-label="文字颜色"
            label=""
            value={textColor}
            onChange={setFillColor}
            variant="secondary"
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <label className={labelClass}>文本对齐</label>
        <ToggleButtonGroup
          aria-label="水平文本对齐"
          selectionMode="single"
          selectedKeys={new Set([textAlign])}
          onSelectionChange={(keys) => {
            const next = selectionToSingleKey(keys);
            if (next) onChange({ textAlign: next as ParagraphStyle['textAlign'] });
          }}
          size="sm"
          className="flex min-w-0 flex-1"
        >
          <ToggleButton id="left" aria-label="左对齐" className="aspect-square flex-1 p-0">
            <IconAlignLeft2 className="h-4 w-4" stroke={1.5} />
          </ToggleButton>
          <ToggleButton id="center" aria-label="居中对齐" className="aspect-square flex-1 p-0">
            <IconAlignCenter className="h-4 w-4" stroke={1.5} />
          </ToggleButton>
          <ToggleButton id="right" aria-label="右对齐" className="aspect-square flex-1 p-0">
            <IconAlignRight2 className="h-4 w-4" stroke={1.5} />
          </ToggleButton>
          <ToggleButton id="justify" aria-label="两端对齐" className="aspect-square flex-1 p-0">
            <IconAlignJustified className="h-4 w-4" stroke={1.5} />
          </ToggleButton>
        </ToggleButtonGroup>
      </div>
      <div className="flex items-center gap-2">
        <label className={labelClass}>盒内位置</label>
        <ToggleButtonGroup
          aria-label="盒内垂直对齐"
          selectionMode="single"
          selectedKeys={new Set([verticalAlign])}
          onSelectionChange={(keys) => {
            const next = selectionToSingleKey(keys);
            if (next) onChange({ verticalAlign: next as NonNullable<ParagraphStyle['verticalAlign']> });
          }}
          size="sm"
          className="flex min-w-0 flex-1"
        >
          <ToggleButton id="top" aria-label="顶" className="flex-1">
            顶
          </ToggleButton>
          <ToggleButton id="middle" aria-label="中" className="flex-1">
            中
          </ToggleButton>
          <ToggleButton id="bottom" aria-label="底" className="flex-1">
            底
          </ToggleButton>
        </ToggleButtonGroup>
      </div>
      <div className="flex items-center gap-2">
        <label className={labelClass}>是否竖排</label>
        <Switch
          aria-label="是否竖排"
          isSelected={isVerticalWriting}
          onChange={(checked) =>
            onChange({ writingMode: checked ? 'vertical-rl' : 'horizontal-tb' })
          }
        >
          <Switch.Content>
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
          </Switch.Content>
        </Switch>
      </div>
    </div>
  );
}
